import React, { useCallback, useMemo, useState } from 'react';
import { utmVisitService } from '../../api/services/UtmVisitService';
import type { UtmStatsViewModel } from '../../api/types/utm';

const toDateInputValue = (value: Date): string => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const buildPresetRange = (daysBack: number): { from: string; to: string } => {
  const today = new Date();
  const from = new Date(today);
  from.setDate(from.getDate() - (daysBack - 1));
  return {
    from: toDateInputValue(from),
    to: toDateInputValue(today),
  };
};

const toIsoStartOfDay = (dateInput: string): string =>
  new Date(`${dateInput}T00:00:00`).toISOString();
const toIsoEndOfDay = (dateInput: string): string =>
  new Date(`${dateInput}T23:59:59.999`).toISOString();

interface SourceRow {
  key: keyof UtmStatsViewModel['counts'];
  label: string;
  icon: string;
  color: string;
}

const SOURCE_ROWS: readonly SourceRow[] = [
  { key: 'whatsapp', label: 'WhatsApp', icon: 'fab fa-whatsapp', color: 'text-success' },
  { key: 'facebook', label: 'Facebook', icon: 'fab fa-facebook', color: 'text-primary' },
  { key: 'instagram', label: 'Instagram', icon: 'fab fa-instagram', color: 'text-danger' },
  { key: 'google', label: 'Google', icon: 'fab fa-google', color: 'text-warning' },
] as const;

const UtmSourcesPage: React.FC = () => {
  const initialRange = useMemo(() => buildPresetRange(7), []);
  const [from, setFrom] = useState<string>(initialRange.from);
  const [to, setTo] = useState<string>(initialRange.to);
  const [stats, setStats] = useState<UtmStatsViewModel | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const rangeInvalid = from !== '' && to !== '' && from > to;

  const applyPreset = useCallback((daysBack: number) => {
    const range = buildPresetRange(daysBack);
    setFrom(range.from);
    setTo(range.to);
  }, []);

  const handleApply = useCallback(
    async (evt: React.FormEvent<HTMLFormElement>) => {
      evt.preventDefault();
      if (rangeInvalid || !from || !to) {
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const result = await utmVisitService.getStats(
          toIsoStartOfDay(from),
          toIsoEndOfDay(to),
        );
        setStats(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'İstatistik yüklenemedi.');
        setStats(null);
      } finally {
        setLoading(false);
      }
    },
    [from, to, rangeInvalid],
  );

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h2 className="mb-0">
          <i className="fas fa-chart-line me-2 text-primary" />
          UTM Kaynakları
        </h2>
      </div>

      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <div className="mb-3 d-flex flex-wrap gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => applyPreset(1)}
            >
              Son 24 saat
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => applyPreset(7)}
            >
              Son 7 gün
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => applyPreset(30)}
            >
              Son 30 gün
            </button>
          </div>

          <form onSubmit={handleApply} className="row g-3 align-items-end" noValidate>
            <div className="col-md-4">
              <label htmlFor="utm-from" className="form-label">
                Başlangıç
              </label>
              <input
                id="utm-from"
                type="date"
                className={`form-control ${rangeInvalid ? 'is-invalid' : ''}`}
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                required
              />
            </div>
            <div className="col-md-4">
              <label htmlFor="utm-to" className="form-label">
                Bitiş
              </label>
              <input
                id="utm-to"
                type="date"
                className={`form-control ${rangeInvalid ? 'is-invalid' : ''}`}
                value={to}
                onChange={(e) => setTo(e.target.value)}
                required
              />
              {rangeInvalid && (
                <div className="invalid-feedback d-block">
                  Başlangıç tarihi bitiş tarihinden büyük olamaz.
                </div>
              )}
            </div>
            <div className="col-md-4">
              <button
                type="submit"
                className="btn btn-primary w-100"
                disabled={rangeInvalid || loading || !from || !to}
              >
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    />
                    Yükleniyor…
                  </>
                ) : (
                  <>
                    <i className="fas fa-search me-2" />
                    Uygula
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          <i className="fas fa-exclamation-triangle me-2" />
          {error}
        </div>
      )}

      <div className="card shadow-sm">
        <div className="card-body">
          {!stats && !loading && !error && (
            <p className="text-muted mb-0">
              Sonuçları görmek için bir tarih aralığı seçin ve Uygula'ya tıklayın.
            </p>
          )}
          {stats && (
            <div className="table-responsive">
              <table className="table table-borderless align-middle mb-0">
                <thead>
                  <tr className="text-muted small text-uppercase">
                    <th>Kaynak</th>
                    <th className="text-end">Ziyaret</th>
                  </tr>
                </thead>
                <tbody>
                  {SOURCE_ROWS.map((row) => (
                    <tr key={row.key}>
                      <td>
                        <i className={`${row.icon} me-2 ${row.color}`} />
                        {row.label}
                      </td>
                      <td className="text-end fw-semibold">
                        {stats.counts[row.key].toLocaleString('tr-TR')}
                      </td>
                    </tr>
                  ))}
                  <tr className="border-top">
                    <td className="fw-bold">Toplam</td>
                    <td className="text-end fw-bold">
                      {stats.total.toLocaleString('tr-TR')}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UtmSourcesPage;
